/**
 * Edit Contact Handler
 * Edit handler for updating contact information
 */

import type { ToolExecutionContext } from '../../types';
import type { EditPreview } from '../../types';
import { EditHandlerBase } from '../base';
import { uuidField, objectField } from '../../utils/schema-builder';
import { generateDiff, hasChanges } from '../../utils/diff-generator';
import { generateConfirmationToken } from '../../utils/confirmation-token';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * Edit contact handler
 * Fetches current contact via GraphQL and generates preview with RLS enforcement
 */
export class EditContactHandler extends EditHandlerBase<any> {
  id = 'edit_contact';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the contact to edit'),
      updates: objectField('Fields to update (name, email, phone, etc.)'),
    },
    required: ['id', 'updates'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    const { id, updates } = params;

    if (!id) {
      throw new Error('Contact ID is required');
    }

    if (!updates || Object.keys(updates).length === 0) {
      throw new Error('No updates provided');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for contact edit');
    }

    try {
      // Dynamically import GraphQL types and documents
      const { GetContactsDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Fetch current contact
      const result = await client.query({
        query: GetContactsDocument,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const currentContact = data?.contactsCollection?.edges?.[0]?.node;
      
      if (!currentContact) {
        throw new Error(`Contact with ID "${id}" not found or you don't have permission to edit it`);
      }

      // Generate diff: compare current to merged (patch semantics — only changed fields)
      const merged = { ...currentContact, ...updates };
      const changes = generateDiff(currentContact, merged);

      if (!hasChanges(changes)) {
        throw new Error('No changes detected');
      }

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'contact',
        entityId: id,
        changes: updates,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'contact',
        id,
        changes,
        confirmationToken: token,
        preview: merged,
      };
    } catch (error) {
      throw new Error(`Contact edit failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
